import type { KUTCResultEntry, FastestTimeRecord } from '../services/kutcResultsService';

export interface MilestoneRecord extends FastestTimeRecord {
  completedLoops: number;
  enteredRace: string;
  source: 'result' | 'checkpoint';
  rank: number;
}
export interface MilestoneCheckpoint {
  personId?: number;
  firstName?: string;
  lastName?: string;
  loopNumber: number;
  isFinishCp: boolean;
  cumulativeTimeSeconds: number;
}
export interface MilestoneEdition {
  id: string;
  year: number;
  total: KUTCResultEntry[];
  races: { loops: number; name: string; results: KUTCResultEntry[] }[];
  checkpoints: MilestoneCheckpoint[];
}
const name = (r: { firstName?: string; lastName?: string }) =>
  `${r.firstName || ''} ${r.lastName || ''}`.normalize('NFKC').trim().replace(/\s+/g, ' ').toLocaleLowerCase();
const positive = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n) && n > 0;
const validStatus = (s?: string) => ['finished', 'dnf'].includes((s || '').toLowerCase());
const display = (seconds: number) => {
  const s = Math.floor(seconds);
  return [Math.floor(s / 3600), Math.floor(s / 60) % 60, s % 60].map(v => String(v).padStart(2, '0')).join(':');
};

/** Edition-scoped identities only. Published results take precedence over raw timing precision. */
export function buildMilestoneRecords(editions: MilestoneEdition[]): Map<number, MilestoneRecord[]> {
  const all: MilestoneRecord[] = [];
  for (const edition of editions) {
    const totals = new Map<number, KUTCResultEntry>();
    const ambiguous = new Set<number>();
    for (const r of edition.total) {
      if (totals.has(r.personId)) ambiguous.add(r.personId);
      totals.set(r.personId, r);
    }
    const candidates = new Map<string, MilestoneRecord>();
    const add = (r: KUTCResultEntry, loops: number, seconds: number, enteredRace: string, source: MilestoneRecord['source']) => {
      const total = totals.get(r.personId);
      if (!total || ambiguous.has(r.personId) || !positive(r.personId) || !Number.isInteger(r.personId) ||
          !name(r) || name(r) !== name(total) || !validStatus(total.status) ||
          !Number.isInteger(loops) || loops < 4 || loops % 4 !== 0 ||
          !Number.isInteger(total.loopsCompleted) || loops > total.loopsCompleted! || !positive(seconds)) return;
      const key = `${r.personId}:${loops}`;
      if (candidates.has(key)) return;
      candidates.set(key, {
        personId: r.personId, firstName: r.firstName || '', lastName: r.lastName || '',
        distanceKey: `${loops}-loops`, raceName: `${loops} loops`, loops,
        timeSeconds: Math.floor(seconds), timeDisplay: display(seconds), editionId: edition.id, year: edition.year,
        completedLoops: total.loopsCompleted!, enteredRace, source, rank: 0
      });
    };
    // Registered distance finish, even when the runner continued afterwards.
    for (const race of edition.races) for (const r of race.results) {
      if ((r.status || '').toLowerCase() === 'finished' && positive(r.raceTimeSeconds)) {
        add(r, race.loops, r.raceTimeSeconds, race.name, 'result');
      }
    }
    for (const r of edition.total) {
      const entered = edition.races.find(race => race.results.some(row => row.personId === r.personId && name(row) === name(r)));
      const enteredRace = entered?.name || r.raceName || '';
      if (positive(r.totalTimeSeconds)) add(r, r.loopsCompleted!, r.totalTimeSeconds, enteredRace, 'result');
      const finishes = edition.checkpoints.filter(cp => cp.personId === r.personId && cp.isFinishCp === true);
      const byLoop = new Map<number, MilestoneCheckpoint[]>();
      for (const cp of finishes) {
        const rows = byLoop.get(cp.loopNumber) || [];
        rows.push(cp); byLoop.set(cp.loopNumber, rows);
      }
      // Do not infer missing loop numbers or silently select one of conflicting finishes.
      let previous = 0;
      for (let loop = 1; loop <= (r.loopsCompleted || 0); loop++) {
        const rows = byLoop.get(loop);
        if (rows?.length !== 1) break;
        const cp = rows[0];
        if (name(cp) !== name(r) || !positive(cp.cumulativeTimeSeconds) || cp.cumulativeTimeSeconds <= previous ||
            (positive(r.totalTimeSeconds) && cp.cumulativeTimeSeconds > r.totalTimeSeconds + 1)) break;
        previous = cp.cumulativeTimeSeconds;
        add(r, loop, cp.cumulativeTimeSeconds, enteredRace, 'checkpoint');
      }
    }
    all.push(...Array.from(candidates.values()));
  }
  const grouped = new Map<number, MilestoneRecord[]>();
  for (const row of all) grouped.set(row.loops, [...(grouped.get(row.loops) || []), row]);
  for (const [loops, rows] of Array.from(grouped)) {
    rows.sort((a, b) => a.timeSeconds - b.timeSeconds || a.year - b.year || a.personId - b.personId);
    let rank = 0;
    rows.forEach((row, index) => {
      if (index === 0 || row.timeSeconds !== rows[index - 1].timeSeconds) rank = index + 1;
      row.rank = rank;
    });
    grouped.set(loops, rows.filter(row => row.rank <= 3));
  }
  return new Map(Array.from(grouped).sort(([a], [b]) => a - b));
}
